import { useCallback, useEffect, useState } from "react";
import { isCapacitor } from "../lib/capacitor";
import { enablePushNotifications } from "../lib/pwa";
import {
  bannerOnderdrukt,
  markeerGevraagd,
  meldingStand,
  onderdrukBanner,
  openAppInstellingen,
  vraagBatterijVrijstelling,
  type MeldingStand,
} from "../lib/meldingen";
import { rechtenRonde } from "../lib/rechten";

/**
 * Vraagt uit zichzelf om wat er nodig is om een melding aan te laten komen.
 *
 * Tot nu toe deed de app dat niet: er zat een knopje "meldingen aanzetten" in
 * de statusbalk, en wie dat niet zag kreeg nooit iets binnen zonder te weten
 * waarom. Over batterijbeheer zei de app helemaal niets, terwijl dat de reden
 * is dat een bericht na een paar stille dagen alsnog niet doorkomt.
 *
 * De volgorde is de volgorde waarin het misgaat: eerst de permissie, want
 * zonder dat is de rest zinloos, en dan pas het batterijbeheer. Eén ding
 * tegelijk - twee vragen naast elkaar leest niemand.
 *
 * Wegklikken mag, en dan blijft het veertien dagen stil. Via de instellingen,
 * tab Meldingen, is alles altijd alsnog te regelen.
 */
export default function MeldingenBanner() {
  const [stand, setStand] = useState<MeldingStand | null>(null);
  const [weg, setWeg] = useState(() => bannerOnderdrukt());
  const [bezig, setBezig] = useState(false);

  const lees = useCallback(async () => {
    if (!isCapacitor()) return;
    setStand(await meldingStand());
  }, []);

  useEffect(() => {
    void lees();
  }, [lees]);

  /* Na de systeemdialoog komt de app terug uit de achtergrond. Dat is het enige
     moment waarop we de nieuwe stand kunnen lezen - de dialoog zelf vertelt ons
     niets. */
  useEffect(() => {
    if (!isCapacitor()) return;
    const opnieuw = () => {
      if (document.visibilityState === "visible") void lees();
    };
    document.addEventListener("visibilitychange", opnieuw);
    window.addEventListener("focus", opnieuw);
    return () => {
      document.removeEventListener("visibilitychange", opnieuw);
      window.removeEventListener("focus", opnieuw);
    };
  }, [lees]);

  /* Eén keer per installatie vragen we uit onszelf wat er nog nooit gevraagd
     is - meldingen, microfoon, locatie - zoals elke andere app dat bij de eerste
     start doet. Daarna niet meer; wat er dan nog ontbreekt staat in de
     instellingen, tab Rechten. De ronde zelf staat in lib/rechten.ts. */
  useEffect(() => {
    if (!isCapacitor()) return;
    void rechtenRonde().then(() => void lees());
  }, [lees]);

  if (!isCapacitor() || !stand || weg) return null;

  /* Alleen zeuren over wat we zeker weten. Een stand die niet te lezen was is
     geen reden om iemand lastig te vallen - dan staat het in de instellingen,
     met de reden erbij. */
  const permissieOk = stand.permissie === "granted" || stand.permissie === "onbekend";
  const permissieGeweigerd = stand.permissie === "denied";
  const batterijOk = !stand.batterij || !stand.batterij.supported || stand.batterij.ignoring;
  if (permissieOk && batterijOk) return null;

  function verberg() {
    onderdrukBanner();
    setWeg(true);
  }

  async function doe(actie: () => Promise<unknown>) {
    setBezig(true);
    try {
      await actie();
    } finally {
      setBezig(false);
      void lees();
    }
  }

  const inhoud = !permissieOk
    ? {
        titel: "Meldingen staan uit",
        tekst: permissieGeweigerd
          ? "Je hebt meldingen eerder geweigerd. Android laat dat alleen in de instellingen van de app terugdraaien."
          : "Zonder toestemming laat Swift geen bericht zien als de app dicht is.",
        knop: permissieGeweigerd ? "Naar instellingen" : "Aanzetten",
        actie: permissieGeweigerd
          ? openAppInstellingen
          : async () => {
              markeerGevraagd();
              await enablePushNotifications();
            },
      }
    : {
        titel: "Meldingen kunnen dagen te laat komen",
        tekst:
          "Android zet Swift in slaap als je hem een paar dagen niet opent. Zet hem buiten het batterijbeheer, dan komt een bericht meteen aan.",
        knop: "Toestaan",
        actie: vraagBatterijVrijstelling,
      };

  return (
    <div className="shrink-0 border-b border-amber-800/60 bg-amber-950/40 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-amber-200">🔔 {inhoud.titel}</div>
          <div className="text-xs text-amber-100/70">{inhoud.tekst}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => void doe(inhoud.actie)}
            disabled={bezig}
            className="rounded bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-50"
          >
            {bezig ? "…" : inhoud.knop}
          </button>
          <button
            onClick={verberg}
            className="rounded px-2 py-2 text-xs text-amber-200/70 hover:text-amber-100"
          >
            Niet nu
          </button>
        </div>
      </div>
    </div>
  );
}
